import { getYear, sub } from '@e3dc-react/shell/Libs/date'
import React, { FC, memo, useState } from 'react'
import { useCheckInstallationLocation } from 'src/Libs/dynamic-power-prices'
import { PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import { useCo2Savings } from '../../../Elements/Dashboard/utils'
import { useSummaryData } from '../hooks/use-summary-data'
import useThreeSixtySavingDataCardProps from '../hooks/use-three-sixty-saving-data-card-props'
import { SummaryTimeView } from '../types'
import { calculateSummaryData } from '../utils'
import SummaryDataContent from './SummaryDataContent'

interface Props {
  paperClassName?: string
  powermeterHardwareProducers: PowermeterHardwareModel[]
  hide: boolean
}

/**
 * Displays a summary of values such as Co2 Savings for different views (e.g. today)
 */
const SummaryData: FC<Props> = ({ paperClassName, powermeterHardwareProducers, hide }) => {
  const [period, setPeriod] = useState<SummaryTimeView>('day')
  const {
    powermeterProductionValue,
    differences,
    previousDifferences,
    previousPowermeterProductionValue,
    isLoading: isSummaryDataLoading,
  } = useSummaryData(period, powermeterHardwareProducers)
  const co2Savings = useCo2Savings(getYear(new Date()), (differences?.diff?.Consumption ?? 0) - (differences?.diff?.NetOut ?? 0))
  const previousCo2Saving = useCo2Savings(
    getYear(sub(new Date(), { years: 1 })),
    (previousDifferences?.diff?.Consumption ?? 0) - (previousDifferences?.diff?.NetOut ?? 0)
  )

  const { co2WithUnit, networkWithUnit, productionWithUnit } = calculateSummaryData(co2Savings, powermeterProductionValue, differences)
  const {
    co2WithUnit: previousCo2WithUnit,
    networkWithUnit: previousNetworkWithUnit,
    productionWithUnit: previousProductionWithUnit,
  } = calculateSummaryData(previousCo2Saving, previousPowermeterProductionValue, previousDifferences)

  const threeSixtySavingCardProps = useThreeSixtySavingDataCardProps(period)
  const { isDppCountryInstallation } = useCheckInstallationLocation()
  const isDppSupported = threeSixtySavingCardProps.isDppEnabled || isDppCountryInstallation

  const handleSetPeriod = (newPeriod: SummaryTimeView): void => {
    setPeriod(newPeriod)
  }

  if (hide) return null

  return (
    <SummaryDataContent
      paperClassName={paperClassName}
      threeSixtySavingCardProps={threeSixtySavingCardProps}
      co2WithUnit={co2WithUnit}
      networkWithUnit={networkWithUnit}
      productionWithUnit={productionWithUnit}
      previousCo2WithUnit={previousCo2WithUnit}
      previousNetworkWithUnit={previousNetworkWithUnit}
      previousProductionWithUnit={previousProductionWithUnit}
      isDppSupported={isDppSupported}
      isSummaryDataLoading={isSummaryDataLoading}
      handleSetPeriod={handleSetPeriod}
    />
  )
}

export default memo(SummaryData)
